import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { logoutUser } from "./AccountLogoutThunk";

const API_BASE = process.env.REACT_APP_API_URL;

/* ------------------- THUNKS ------------------- */

// GET /wallet/balance
export const fetchWalletBalance = createAsyncThunk(
  "wallet/fetchBalance",
  async (memberID, thunkAPI) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/wallet/balance?member_id=${memberID}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-user-role": "admin",
          },
          // No body needed
        }
      );

      if (res.status === 401) {
        thunkAPI.dispatch(logoutUser());
        window.location.href = process.env.REACT_APP_AUTH_BASE_URL;
        return thunkAPI.rejectWithValue("Unauthorized");
      }

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.message || "Failed to fetch balance");
      }

      return json;
    } catch (error) {
      throw error;
    }
  }
);

// GET /wallet/history
export const fetchWalletHistory = createAsyncThunk(
  "wallet/fetchHistory",
  async (_, thunkAPI) => {
    const res = await fetch(`${API_BASE}/api/wallet/history`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin",
      },
    });

    if (res.status === 401) {
      thunkAPI.dispatch(logoutUser());
      window.location.href = process.env.REACT_APP_AUTH_BASE_URL;
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || "Failed to fetch history");
    return json;
  }
);

// POST /wallet/payout
export const createWalletPayout = createAsyncThunk(
  "wallet/createPayout",
  async (payload, thunkAPI) => {
    const res = await fetch(`${API_BASE}/api/wallet/payout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin",
      },
      body: JSON.stringify(payload),
    });

    if (res.status === 401) {
      thunkAPI.dispatch(logoutUser());
      window.location.href = process.env.REACT_APP_AUTH_BASE_URL;
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || "Failed to create payout");


    return json;
  }
);
// POST /wallet/payout
export const createWalletCharityPayout = createAsyncThunk(
  "wallet/createCharityPayout",
  async (payload, thunkAPI) => {
    const res = await fetch(`${API_BASE}/api/wallet/admin-debit`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin",
      },
      body: JSON.stringify(payload),
    });

    if (res.status === 401) {
      thunkAPI.dispatch(logoutUser());
      window.location.href = process.env.REACT_APP_AUTH_BASE_URL;
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || "Failed to create payout");


    return json;
  }
);

/* ------------------- SLICE ------------------- */

const initialState = {
  balance: null,
  history: [],
  payoutResponse: null,
  loading: false,
  error: null,
};

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    clearWalletError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Balance
      .addCase(fetchWalletBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWalletBalance.fulfilled, (state, action) => {
        state.loading = false;

        // More explicit handling
        if (action.payload && typeof action.payload.balance !== "undefined") {
          state.balance = action.payload.balance;
        } else {
          state.balance = null;
        }
      })
      .addCase(fetchWalletBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load balance";
      })

      // History
      .addCase(fetchWalletHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWalletHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload || [];
      })
      .addCase(fetchWalletHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load history";
      })

      // Payout
      .addCase(createWalletPayout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWalletPayout.fulfilled, (state, action) => {
        state.loading = false;
        state.payoutResponse = action.payload;

        // If payout returns updated balance, update it
        if (action.payload && typeof action.payload.balance !== "undefined") {
          state.balance = action.payload.balance;
        }
      })
      .addCase(createWalletPayout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create payout";
      })

      //Charity Payout
      .addCase(createWalletCharityPayout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWalletCharityPayout.fulfilled, (state, action) => {
        state.loading = false;
        state.payoutResponse = action.payload;

        // If payout returns updated balance, update it
        if (action.payload && typeof action.payload.balance !== "undefined") {
          state.balance = action.payload.balance;
        }
      })
      .addCase(createWalletCharityPayout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create payout";
      });
  },
});

export const { clearWalletError } = walletSlice.actions;
export default walletSlice.reducer;
